import React, { useRef, useEffect, useState, useContext } from 'react'
import { useFrame } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { floatingAnimation } from './animations'
import styles from './styles.module.css'
import { orbData } from './OrbField'

// Create a context to manage global hover state
const OrbContext = React.createContext()

export const OrbProvider = ({ children }) => {
  const [activeOrb, setActiveOrb] = useState(null)
  return (
    <OrbContext.Provider value={{ activeOrb, setActiveOrb }}>
      {children}
    </OrbContext.Provider>
  )
}

export const Orb = ({ position, data, onOrbClick }) => {
  const orbRef = useRef()
  const mainSphereRef = useRef()
  const [hovered, setHovered] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const { setActiveOrb } = useContext(OrbContext)

  // Initialize floating animation
  useEffect(() => {
    floatingAnimation(orbRef, Math.random() * 2)
  }, [])

  // Handle tooltip visibility with delay
  useEffect(() => {
    let timeout
    if (hovered) {
      timeout = setTimeout(() => {
        setTooltipVisible(true)
      }, 100)
    } else {
      setTooltipVisible(false)
    }
    return () => clearTimeout(timeout)
  }, [hovered])

  const handlePointerOver = (event) => {
    event.stopPropagation()
    setHovered(true)
    setActiveOrb(data.id)
  }

  const handlePointerOut = (event) => {
    event.stopPropagation()
    setHovered(false)
    setActiveOrb(null)
    if (orbRef.current) {
      orbRef.current.scale.setScalar(1)
    }
  }

  // Handle hover scale effect
  useFrame(() => {
    if (orbRef.current) {
      const targetScale = hovered ? 1.4 : 1
      const currentScale = orbRef.current.scale.x
      const newScale = THREE.MathUtils.lerp(currentScale, targetScale, 0.1)
      orbRef.current.scale.setScalar(newScale)
    }
  })

  // Add this helper function to get connected technology names
  const getConnectedTechNames = () => {
    if (!data.connections || !data.connections.length) return []
    
    // Get the names of connected technologies from orbData
    return data.connections.map(id => {
      const connectedTech = orbData.find(orb => orb.id === id)
      return connectedTech ? connectedTech.title : `Unknown (${id})`
    })
  }

  return (
    <group
      ref={orbRef}
      position={position}
      onClick={(e) => {
        e.stopPropagation()
        onOrbClick(data)
      }}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onPointerMissed={() => setHovered(false)}
    >
      {/* Main orb sphere */}
      <mesh ref={mainSphereRef}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshPhongMaterial
          color={data.color}
          transparent
          opacity={0.95}
          shininess={150}
          emissive={data.color}
          emissiveIntensity={0.2}
        />
      </mesh>

      {/* Updated Tooltip */}
      {hovered && (
        <Html center>
          <div className={`${styles.orbTooltip} ${tooltipVisible ? styles.visible : ''}`}>
            <h3>{data.title}</h3>
            <h4 className={styles.tooltipSubtitle}>Interconnectivity</h4>
            <div className={styles.connectionsList}>
              {getConnectedTechNames().map((tech, index) => (
                <li key={index} className={styles.connectionItem}>{tech}</li>
              ))}
            </div>
          </div>
        </Html>
      )}
    </group>
  )
}